import { Flex, Switch, Typography } from "antd";
import { useTranslate } from "@refinedev/core";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { useTemplateData } from "../../../../hooks/useTemplateData";
import { TemplateType } from "../../../../types";

export const TransitionConfigFeature = ({
  hasTransition,
}: {
  hasTransition: boolean | undefined;
}) => {
  const t = useTranslate();
  const {
    storyboardSections,
    mediaGenerationSettingsData: {
      mediaGenerationSettings,
      setAllChaptersTransitionVisibility,
      isMediaSettingsSaving,
    },
  } = useTemplateData();

  const { currentChapter, setCurrentChapter } = useTemplateEditorStore(
    (store) => ({
      currentChapter: store.currentChapter,
      setCurrentChapter: store.setCurrentChapter,
    })
  );
  const resetTemplateUpdates = useTemplatesStatusStore(
    (state) => state.resetTemplateUpdates
  );

  // Aggregate per-chapter state: ON only when every section's chapter has
  // include_transition !== false. Falls back to project-level hasTransition
  // only for chapters that have no media-settings row yet (legacy seed).
  const switchChecked = storyboardSections.every((section) => {
    const chapter = mediaGenerationSettings?.chapters.find(
      (c) => c.chapter_id === section.id
    );
    return (chapter?.include_transition ?? hasTransition) === true;
  });

  const handleToggle = async (nextValue: boolean) => {
    await setAllChaptersTransitionVisibility(
      nextValue,
      storyboardSections.map((section) => section.id)
    );

    if (!nextValue && currentChapter.type === TemplateType.TRANSITION) {
      // Rebuild from chapter_template_id instead of spreading currentChapter,
      // to avoid pairing type: CHAPTER with the transition's templateId.
      const chapterTemplateId = mediaGenerationSettings?.chapters.find(
        (chapter) => chapter.chapter_id === currentChapter.sectionId
      )?.chapter_template_id;

      resetTemplateUpdates();
      setCurrentChapter({
        templateId: chapterTemplateId ?? undefined,
        topic: currentChapter.topic,
        sectionId: currentChapter.sectionId,
        type: TemplateType.CHAPTER,
      });
    }
  };

  const handleChange = (nextValue: boolean) => {
    handleToggle(nextValue).catch((error) =>
      console.error("Error switching transition", error)
    );
  };

  return (
    <Flex align="center" justify="space-between">
      <Typography.Text>
        {t("components.templates.settings.enableAllTransitions")}
      </Typography.Text>
      <Switch
        checked={switchChecked}
        size="small"
        disabled={isMediaSettingsSaving}
        onChange={handleChange}
      />
    </Flex>
  );
};
